import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {CardBody} from "@material-tailwind/react";
import Card from "../../../components/Card";
import PageBlock from "../../../components/PageBlock";
import DropdownInput from "../../../components/DropdownInput";
import {useUserContext} from "../../../types/contexts/UserContext";
import {FooterListItem} from "../../../components/Sidebar/FooterMenu";
import SafeChargeForm from "./SafeChargeForm";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import BrokerAvatar from "../../../components/BrokerAvatar";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {
    WithdrawBrokersListResponse,
    WithdrawClient,
    WithdrawClientsResponse, WithdrawProvidersResponse
} from "../../../types/Responses/WithdrawsResponse";
import PayablForm from "./PayablForm";
import AjaxDropdownInput from "../../../components/AjaxDropdownInput";
import CountryAvatar from "../../../components/CountryAvatar";
import MonetixForm from "./MonetixForm";
import WallettecForm from "./WallettecForm";
import {useTranslation} from "react-i18next";
import SkrillForm from "./SkrillForm";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import AwePayForm from "./AwePayForm";
import ZotaPayForm from "./ZotaPayForm";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import UniPaymentForm from "./UniPaymentForm";
import LimePayForm from "./LimePayForm";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../../reducers/sidebar";
import BVNKForm from "./BVNKForm";
import B2BinPayForm from "./B2BinPayForm";

export const emailRegex = '^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|.(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))';
export const nameRegEx = '^(?=.{1,50}$)[a-zA-Z]+(?:[\'_.\\s][a-zA-Z]+)*$';
const WithdrawsForm = () => {

    const {t} = useTranslation();
    const user = useUserContext();

    const toast = useToastContext();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const [provider, setProvider] = useState<WithdrawProvider>();
    const [brokers, setBrokers] = useState<WithdrawBrokersListResponse>();
    const [clients, setClients] = useState<WithdrawClientsResponse>();
    const [broker, setBroker] = useState<number>();
    const [client, setClient] = useState<WithdrawClient>();
    const [providers, setProviders] = useState<WithdrawProvidersResponse>();
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);

    const getBrokers = useCallback(() => {
        API.getWithdrawBrokersList().then((response: AxiosResponse) => {
            setBrokers(response.data as WithdrawBrokersListResponse);
            if (response.data.data.length === 1) {
                setBroker(response.data.data[0].id);
            }
            setFormIsReady(true);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_BROKERS_LIST}`, toast, handleError, setIsForbidden));
    }, [toast, handleError]);

    const getProviders = useCallback(() => {
        API.getWithdrawProviders().then((response: AxiosResponse) => {
            setProviders(response.data as WithdrawProvidersResponse)
            setFormIsReady(true);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_PROVIDERS}`, toast, handleError, setIsForbidden));
    }, [toast, handleError]);


    const changeProvider = useCallback((s: string | number) => {
        // @ts-expect-error can be other type
        if (!Object.entries(WithdrawProvider).find(c => c.includes(s)))
            return;
        // @ts-expect-error can be other type
        setProvider(typeof s !== "string" ? s.toString() : s);
    }, [setProvider]);

    const changeBroker = useCallback((u: string | number) => {
        setBroker(typeof u === "string" ? parseInt(u) : u);
    }, [setBroker]);

    const changeClient = useCallback((u: string | number) => {
        if (!clients)
            return;
        const selectedClient = clients.data.find(c => c.id === u);
        if (!selectedClient)
            return;
        if (!Object.entries(WithdrawProvider).find(c => c.includes(selectedClient.info.provider.toLowerCase())))
            return;
        setClient(selectedClient);
        console.log(selectedClient);
        console.log(selectedClient.info.provider.toLowerCase());
        changeProvider(selectedClient.info.provider.toLowerCase());
    }, [setClient, clients, changeProvider]);

    const searchClient = useCallback((query: string) => {
        if (query.length > 3) {
            API.searchWithdrawClient(query, broker ?? 0).then((response: AxiosResponse) => {
                setClients(response.data as WithdrawClientsResponse)
            }).catch(e => ErrorHandler(e, `get${APILink.SEARCH_WITHDRAW_CLIENT}`, toast, handleError, setIsForbidden));
        }
    }, [broker, toast, handleError]);

    const hasPermission = useCallback((permission: string) => {
        if (!user || !user.user || !brokers)
            return false;
        const selectedBroker = brokers.data.find(b => b.id === broker);
        if (!selectedBroker)
            return false;
        return user.user.permissions.includes(permission) && selectedBroker.permissions.includes(permission);
    }, [user, broker, brokers])

    const getProvidersList = useCallback(() => {
        if (!user || !user.user || !brokers || !providers)
            return [];
        const list: FooterListItem[] = [];
        providers.data.forEach((value) => {
            if (value.toLowerCase() === 'payabl') {
                list.push({
                    name: WithdrawProvider.PAYABL.substring(0, 1).toUpperCase() + WithdrawProvider.PAYABL.substring(1),
                    code: WithdrawProvider.PAYABL
                });
            }
            if (value.toLowerCase() === 'zotapay') {
                list.push({
                    name: WithdrawProvider.ZOTAPAY.substring(0, 1).toUpperCase() + WithdrawProvider.ZOTAPAY.substring(1),
                    code: WithdrawProvider.ZOTAPAY
                });
            }
            if (value.toLowerCase() === 'awepay') {
                list.push({
                    name: WithdrawProvider.AWEPAY.substring(0, 1).toUpperCase() + WithdrawProvider.AWEPAY.substring(1),
                    code: WithdrawProvider.AWEPAY
                });
            }
            if (value.toLowerCase() === 'monetix') {
                list.push({
                    name: WithdrawProvider.MONETIX.substring(0, 1).toUpperCase() + WithdrawProvider.MONETIX.substring(1),
                    code: WithdrawProvider.MONETIX
                });
            }
            if (value.toLowerCase() === 'safecharge') {
                list.push({
                    name: WithdrawProvider.SAFECHARGE.substring(0, 1).toUpperCase() + WithdrawProvider.SAFECHARGE.substring(1),
                    code: WithdrawProvider.SAFECHARGE
                });
            }
            if (value.toLowerCase() === 'wallettec') {
                list.push({
                    name: WithdrawProvider.WALLETTEC.substring(0, 1).toUpperCase() + WithdrawProvider.WALLETTEC.substring(1),
                    code: WithdrawProvider.WALLETTEC
                });
            }
            if (value.toLowerCase() === 'neteller') {
                list.push({
                    name: 'Neteller',
                    code: WithdrawProvider.SKRILL
                });
            }
            if (value.toLowerCase() === 'gbpay') {
                list.push({
                    name: WithdrawProvider.GBPAY.substring(0, 1).toUpperCase() + WithdrawProvider.GBPAY.substring(1),
                    code: WithdrawProvider.GBPAY
                });
            }
            if (value.toLowerCase() === 'echelonpay') {
                list.push({
                    name: WithdrawProvider.ECHELONPAY.substring(0, 1).toUpperCase() + WithdrawProvider.ECHELONPAY.substring(1),
                    code: WithdrawProvider.ECHELONPAY
                });
            }
            if (value.toLowerCase() === 'unipayment') {
                list.push({
                    name: WithdrawProvider.UNIPAYMENT.substring(0, 1).toUpperCase() + WithdrawProvider.UNIPAYMENT.substring(1),
                    code: WithdrawProvider.UNIPAYMENT
                });
            }
            if (value.toLowerCase() === 'limepay') {
                list.push({
                    name: WithdrawProvider.LIMEPAY.substring(0, 1).toUpperCase() + WithdrawProvider.LIMEPAY.substring(1),
                    code: WithdrawProvider.LIMEPAY
                });
            }
            if (value.toLowerCase() === 'bvnk') {
                list.push({
                    name: WithdrawProvider.BVNK.substring(0, 1).toUpperCase() + WithdrawProvider.BVNK.substring(1),
                    code: WithdrawProvider.BVNK
                });
            }

            if (value.toLowerCase() === 'b2binpay') {
                list.push({
                    name: WithdrawProvider.B2BINPAY.substring(0, 1).toUpperCase() + WithdrawProvider.B2BINPAY.substring(1),
                    code: WithdrawProvider.B2BINPAY
                });
            }
        });

        return list;
    }, [user, brokers, providers, hasPermission]);

    const providerWithdrawForm = useMemo(() => {
        if (!provider || !broker)
            return <></>

        switch (provider) {
            case WithdrawProvider.SAFECHARGE:
                return <SafeChargeForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.PAYABL:
                return <PayablForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.MONETIX:
                return <MonetixForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.WALLETTEC:
                return <WallettecForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.SKRILL:
                return <SkrillForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.AWEPAY:
                return <AwePayForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.ZOTAPAY:
                return <ZotaPayForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.UNIPAYMENT:
                return <UniPaymentForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.LIMEPAY:
                return <LimePayForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.B2BINPAY:
                return <B2BinPayForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            case WithdrawProvider.BVNK:
                return <BVNKForm broker={broker} clientTransactionID={client?.id ?? 0}/>
                break;
            default :
                return <></>
        }
    }, [provider, client, broker]);

    const formUI = useMemo(() => {
        if (!broker)
            return <></>

        return <div className="max-w-md">
            <DropdownInput name={t('admin.withdraws.provider')} className="w-full min-w-[200px]" selected={provider}
                           onChange={changeProvider}
                           list={getProvidersList()}/>
        </div>
    }, [provider, changeProvider, getProvidersList, broker]);

    const brokerUI = useMemo(() => {
        if (!brokers || !formIsReady || isForbidden || brokers.data.length < 2)
            return <></>
        return <div className="max-w-md mb-4">
            <DropdownInput name={t('admin.withdraws.broker')} list={brokers.data.map((u) => {
                return {name: u.name, id: u.id, icon: <BrokerAvatar image={u.image} size="xs"/>}
            })} selected={broker} searchable={true} onChange={changeBroker}/>
        </div>
    }, [formIsReady, isForbidden, broker, brokers, changeBroker]);

    const clientUI = useMemo(() => {
        if (!formIsReady || isForbidden || !broker)
            return <></>

        return <div className="max-w-md mb-4">
            <div className="mb-2 text-gray-500 text-sm">{t('admin.withdraws.client_message')}</div>
            <AjaxDropdownInput name={t('admin.withdraws.client')} list={clients?.data.map((c) => {
                return {
                    name: c.name, id: c.id, description: <div className="block text-xs w-full">
                        <div>
                            <span
                                className="inline-block mr-2">{t('admin.withdraws.provider')}: {c.info.provider}</span>
                            <span className="inline-block mr-2">{t('admin.withdraws.method')}: {c.info.method}</span>
                            <span className="inline-block mr-2">{t('admin.withdraws.account')}: {c.info.account}</span>
                        </div>
                        <div>
                            <span className="inline-block mr-2">{t('admin.withdraws.country')}: <CountryAvatar
                                country={c.info.country}/></span>
                            <span
                                className="inline-block mr-2">{t('admin.withdraws.currency')}: {c.info.currency.code}</span>
                        </div>
                    </div>
                };
            }) ?? []} selected={client?.id ?? 0} searchable={true} onChange={changeClient} onSearch={searchClient}
                               searchPlaceholder={t('admin.withdraws.client_placeholder')}/>
        </div>
    }, [formIsReady, isForbidden, broker, client, changeClient, searchClient, clients]);

    useEffect(() => {
        setTitle(t('admin.withdraws.title'));
        dispatch(setSidebarTitle(t('admin.withdraws.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                getBrokers();
                getProviders();
            }).catch()
        }
    }, [user, dispatch])

    return <PageBlock>
        {!formIsReady ? <FormSkeleton/> : <>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {brokerUI}
                    {clientUI}
                    {formUI}
                    {providerWithdrawForm}
                </CardBody>
            </Card>
        </>}
    </PageBlock>
}

export default memo(WithdrawsForm);
